import { inject, Injectable } from '@angular/core'
import { AssistanceStatus, AssistanceStatusName } from '../models/assistanceStatus'
import { Assistance } from '../models/assistance'
import { ConfigService } from './config.service'
import { DateUtils } from './date-utils.service'
import { AssistantAssignment, AssistantAssignmentList } from '../models/assistantAssignment'
import { AlertController } from '@ionic/angular'
import { FeathersService } from './feathers.service'
import { Observable } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'
import { differenceInMinutes } from 'date-fns'
import { isNumber } from 'rrule/dist/esm/src/helpers'

@Injectable({
  providedIn: 'root',
})
export class AssistanceStatusManagerService {
  private configService = inject(ConfigService)
  private alertCtrl = inject(AlertController)
  private feathers = inject(FeathersService)

  private TAG: string = 'AssistanceStatusManagerService'

  statuses: AssistanceStatus[]

  statuses$: Observable<AssistanceStatus[]>

  constructor() {
    this.feathers.find(AssistanceStatus).subscribe((s) => {
      this.statuses = s
    })
  }

  init() {
    if (this.statuses$) return

    this.statuses$ = this.feathers.find(AssistanceStatus).pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getStatuses() {
    if (!this.statuses$) this.init()
    return this.statuses$
  }

  getValidatedStatusById(assistantPresent: boolean, statusIdToTryToChangeTo: number): number {
    const s = this.getStatusById(statusIdToTryToChangeTo)
    const validatedStatus = this.getValidatedStatus(assistantPresent, s)
    return validatedStatus.id
  }

  getValidatedStatusByName(
    assistantPresent: boolean,
    statusNameToTryToChangeTo: AssistanceStatusName,
  ): AssistanceStatusName {
    console.log(this.TAG + 'getValidatedStatusByName', statusNameToTryToChangeTo)
    const s = this.getStatusByName(statusNameToTryToChangeTo)
    const validatedStatus = this.getValidatedStatus(assistantPresent, s)
    return validatedStatus.name
  }

  getValidatedStatus(assistantPresent: boolean, statusToTryToChangeTo: AssistanceStatus): AssistanceStatus {
    if (
      statusToTryToChangeTo.name === AssistanceStatusName.PLANNED ||
      statusToTryToChangeTo.name === AssistanceStatusName.CANCELED
    ) {
      return statusToTryToChangeTo // We expect that in this case, the assistant will be cleared
    } else if (statusToTryToChangeTo.name !== AssistanceStatusName.CANCELED_LATE) {
      if (!assistantPresent) {
        console.warn('Trying to advance assistance status but there is no assistant.')
        return this.getStatusByName(AssistanceStatusName.PLANNED)
      }
      return statusToTryToChangeTo
    }

    return statusToTryToChangeTo
  }

  public getStatusByName(name: AssistanceStatusName) {
    return this.statuses.find((status) => status.name === name)
  }

  public getStatusByNameAsync(name: AssistanceStatusName) {
    this.init()

    return this.statuses$
      .pipe(map((statuses: AssistanceStatus[]) => statuses.find((status) => status.name === name)))
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  public getStatusesByNamesAsync(statusNames: AssistanceStatusName[]) {
    this.init()

    return this.statuses$.pipe(
      map((statuses: AssistanceStatus[]) => statuses.filter((s) => statusNames.includes(s.name))),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  getStatusById(id: number | string) {
    if (!this.statuses || !this.statuses.length) throw new Error('Assistance statuses not initialized yet')
    if (!isNumber(id)) {
      id = parseInt(id, 10)
    }
    return this.statuses.find((status) => status.id === id)
  }

  async changeAssistanceStatus(assistance: Assistance, targetStatusName: AssistanceStatusName, force = false) {
    const assistantPresent = assistance.hasAssistant()

    console.log(this.TAG, 'changeStatusOnAssistance to', targetStatusName, assistantPresent)

    function preventStatusChangeIfNoAssistant() {
      if (assistance.assistantAssignments.isEmpty()) {
        console.warn('Trying to advance assistance status but there is no assistant.')
        targetStatusName = AssistanceStatusName.PLANNED
      }
    }

    function preventStatusChangeBasedOnAssignmentStatuses() {
      if (
        !assistance.assistantAssignments
          .getStatuses()
          .every((status) => status && status.name === AssistanceStatusName.SENT_TO_ASSISTANT)
      ) {
        targetStatusName = AssistanceStatusName.HAS_ASSISTANT
      }
    }

    if (!force) {
      if (
        targetStatusName === AssistanceStatusName.CANCELED &&
        (await this.isAssistanceBeingCanceledLate(assistance))
      ) {
        targetStatusName = AssistanceStatusName.CANCELED_LATE
      }
    }

    console.log(this.TAG + 'changeStatusOnAssistance to', targetStatusName, assistantPresent)
    switch (targetStatusName) {
      case AssistanceStatusName.PLANNED:
        assistance.removeAssignments()
        break
      case AssistanceStatusName.HAS_ASSISTANT:
        preventStatusChangeIfNoAssistant()
        break
      case AssistanceStatusName.SENT_TO_ASSISTANT:
        preventStatusChangeIfNoAssistant()
        await this.advanceAssignmentsStatuses(assistance, targetStatusName)
        // TODO: notify assistants of new assistances
        break
      case AssistanceStatusName.CONFIRMED:
        preventStatusChangeIfNoAssistant()
        await this.advanceAssignmentsStatuses(assistance, targetStatusName)
        break
      case AssistanceStatusName.CANCELED:
        // If an assistance is canceled, we need to free up the assistant so he can be assigned somewhere else.
        // But we also need to keep track about which assistant was assigned to this assistance when it was canceled so he can receive payment for it.
        assistance.assistantAssignments.setStatusesTo(this.getStatusByName(AssistanceStatusName.CANCELED))
        break
      case AssistanceStatusName.CANCELED_LATE:
        // If an assistance is canceled late, we need to free up the assistant so he can be assigned somewhere else.
        // But we also need to keep track about which assistant was assigned to this assistance when it was canceled so he can receive payment for it.

        // const fakeAvailability = new AssistantAvailability()
        // fakeAvailability.assistant = assistance.assistant
        // assistance.setAssistantAvailability(fakeAvailability)
        assistance.assistantAssignments.setStatusesTo(this.getStatusByName(AssistanceStatusName.CANCELED_LATE))

        break
      default:
        preventStatusChangeIfNoAssistant()
        break
    }

    const s = this.statuses.find((status) => status.name === targetStatusName)

    assistance.updateStatus(s)
    console.log(assistance)

    return assistance
  }

  getValidatedStatusByIdWhenChangingAssistant(newAssistantPresent: boolean, statusId: number): AssistanceStatus {
    const currentStatus = this.getStatusById(statusId)

    console.log('getValidatedStatusByIdWhenChangingAssistant', newAssistantPresent, currentStatus)

    if (currentStatus.name === AssistanceStatusName.CANCELED) {
      console.warn('should never happen - it shouldnt be possible to change assistant on a canceled assistance')
      return this.getStatusByName(AssistanceStatusName.CANCELED)
    }
    if (currentStatus.name === AssistanceStatusName.CANCELED_LATE) {
      console.warn('should never happen - it shouldnt be possible to change assistant on a canceled assistance')
      console.warn('should never happen')
      return this.getStatusByName(AssistanceStatusName.CANCELED_LATE)
    }
    if (newAssistantPresent) {
      return this.getStatusByName(AssistanceStatusName.HAS_ASSISTANT)
    }
    return this.getStatusByName(AssistanceStatusName.PLANNED)
  }

  getValidatedStatusByIdWhenChangingAssignment(
    assignmentList: AssistantAssignmentList,
    currentGlobalStatusId: number,
  ): AssistanceStatus {
    const currentGlobalStatus = this.getStatusById(currentGlobalStatusId)

    if (!assignmentList) {
      if (currentGlobalStatus.name === AssistanceStatusName.CANCELED) {
        return this.getStatusByName(AssistanceStatusName.CANCELED)
      } else {
        return this.getStatusByName(AssistanceStatusName.PLANNED)
      }
    }

    console.log('getValidatedStatusByIdWhenChangingAssistant', assignmentList, currentGlobalStatus)

    if (currentGlobalStatus.name === AssistanceStatusName.CANCELED) {
      console.warn('should never happen - it shouldnt be possible to change assistant on a canceled assistance')
      return this.getStatusByName(AssistanceStatusName.CANCELED)
    }
    if (currentGlobalStatus.name === AssistanceStatusName.CANCELED_LATE) {
      console.warn('should never happen - it shouldnt be possible to change assistant on a canceled assistance')
      return this.getStatusByName(AssistanceStatusName.CANCELED_LATE)
    }
    if (assignmentList.hasAnyAssistant()) {
      return this.getLowestPositiveStatusFromStatuses(assignmentList.getStatuses())
    }
    return this.getStatusByName(AssistanceStatusName.PLANNED)
  }

  public isLateToCancelAssistance(assistance: Pick<Assistance, 'statusId' | 'getDateFrom'>): Promise<boolean> {
    return this.isAssistanceBeingCanceledLate(assistance)
  }

  /**
   * Checks whether the Assistance would be canceled late if canceled now. Relevant only on CONFIRMED assistances.
   * @param assistance
   * @private
   */
  private async isAssistanceBeingCanceledLate(
    assistance: Pick<Assistance, 'statusId' | 'getDateFrom'>,
  ): Promise<boolean> {
    const status = this.statuses.find((s) => assistance.statusId === s.id)

    if (status.name !== AssistanceStatusName.CONFIRMED) {
      return false
    }

    const limitMinutesToLateCancelAssistance = DateUtils.hoursToMinutes(
      await this.configService.getLateCanceledConfirmedAssistanceLimitHoursToAssistance(),
    )
    const daysListToAssistanceStart = DateUtils.rangeToDays({
      from: new Date(),
      to: assistance.getDateFrom(),
    })

    // Business rules:
    // Assistance is late canceled when plannedFrom is closer than limitMinutesToLateCancelAssistance (configurable in DB), EXCLUDING any holidays or weekend days.
    const minutesToAssistanceStartExcludingHolidaysAndWeekends = daysListToAssistanceStart.reduce(
      (totalMinutes, day) => {
        const accountableMinutesForDay = DateUtils.isWeekendOrHoliday(day.from)
          ? 0
          : differenceInMinutes(day.to, day.from)
        return totalMinutes + accountableMinutesForDay
      },
      0,
    )

    return minutesToAssistanceStartExcludingHolidaysAndWeekends < limitMinutesToLateCancelAssistance
    // throw new Error(`Invalid date, assistanceId ${assistance.id}`)
  }

  async advanceAssignmentsStatuses(assistance: Assistance, targetStatusName: AssistanceStatusName) {
    const globalTargetStatusOrder = this.getStatusByName(targetStatusName).getPositiveStatusOrder()

    for (const a of assistance.assistantAssignments.getAssignmentsPrivateArray()) {
      if (a.status.getPositiveStatusOrder() < globalTargetStatusOrder) {
        await this.advanceAssignmentStatusMutating(a)
      }
    }
  }

  // TODO: should throw on error instead of returning false
  async advanceAssignmentStatusWithGlobalValidationMutating(
    assistance: Assistance,
    assignment: AssistantAssignment,
    withDialog = false,
  ) {
    if (await this.advanceAssignmentStatusMutating(assignment, withDialog)) {
      console.log('dialog true')
      assistance.status = this.getValidatedStatusByIdWhenChangingAssignment(
        assistance.assistantAssignments,
        assistance.status.id,
      )
      return true
    }
    return false
  }

  async advanceAssignmentStatusImmutable(assignment: AssistantAssignment, withDialog = false) {
    let targetStatus: AssistanceStatus
    switch (assignment.status.name) {
      case AssistanceStatusName.HAS_ASSISTANT:
        targetStatus = this.getStatusByName(AssistanceStatusName.SENT_TO_ASSISTANT)
        break
      case AssistanceStatusName.SENT_TO_ASSISTANT:
        targetStatus = this.getStatusByName(AssistanceStatusName.CONFIRMED)
        break
      default:
        return null
    }

    if (withDialog) {
      const alert = await this.alertCtrl.create({
        header: 'Změnit stav?',
        message: `Chcete změnit stav na "${targetStatus.humanReadableName}"?`,
        buttons: [
          {
            text: 'Ne',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              console.log('cancel clicked')
              alert.dismiss({ value: null })
              return false
            },
          },
          {
            text: 'Ano',
            cssClass: 'primary',
            role: 'confirm',
            handler: () => {
              console.log('Ano clicked')
              const newAssignment = assignment.shallowClone()
              newAssignment.setStatusTo(targetStatus)
              alert.dismiss({ value: newAssignment, role: 'confirm' })
              return false
            },
          },
        ],
      })

      await alert.present().then(() => {
        const firstInput: any = document.querySelector('.alert-button.primary')
        firstInput.focus()
        return
      })

      let result = null
      await alert.onDidDismiss().then((data) => {
        console.log('data', data)
        result = data.data?.value
        console.log('result', result)
      })
      console.log('return from dialog', result)
      return result
    } else {
      const newAssignment = assignment.shallowClone()
      newAssignment.setStatusTo(targetStatus)
      return newAssignment
    }
  }

  // TODO: should throw on error instead of returning false
  async advanceAssignmentStatusMutating(assignment: AssistantAssignment, withDialog: boolean = false) {
    let targetStatus: AssistanceStatus
    switch (assignment.status.name) {
      case AssistanceStatusName.HAS_ASSISTANT:
        targetStatus = this.getStatusByName(AssistanceStatusName.SENT_TO_ASSISTANT)
        break
      case AssistanceStatusName.SENT_TO_ASSISTANT:
        targetStatus = this.getStatusByName(AssistanceStatusName.CONFIRMED)
        break
      default:
        return false
    }

    if (withDialog) {
      const alert = await this.alertCtrl.create({
        header: 'Změnit stav?',
        message: `Chcete změnit stav na "${targetStatus.humanReadableName}"?`,
        buttons: [
          {
            text: 'Zrušit',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              console.log('cancel clicked')
              alert.dismiss(false)
              return false
            },
          },
          {
            text: 'Ano',
            cssClass: 'primary',
            role: 'confirm',
            handler: () => {
              console.log('Ano clicked')
              assignment.setStatusTo(targetStatus)
              alert.dismiss({ value: true, role: 'confirm' })
              return false
            },
          },
        ],
      })

      await alert.present().then(() => {
        const firstInput: any = document.querySelector('.alert-button.primary')
        firstInput.focus()
        return
      })

      let result = false
      await alert.onDidDismiss().then((data) => {
        console.log('data', data)
        result = data.data?.role === 'confirm'
        console.log('result', result)
      })
      console.log('return from dialog', result)
      return result
    } else {
      assignment.setStatusTo(targetStatus)
      return true
    }
  }

  private getLowestPositiveStatusFromStatuses(statuses: AssistanceStatus[]): AssistanceStatus {
    return statuses.sort((a, b) => {
      if (a.getPositiveStatusOrder() > b.getPositiveStatusOrder()) return 1
      if (b.getPositiveStatusOrder() > a.getPositiveStatusOrder()) return -1
      return 0
    })[0]
  }
}
